import { Component, ChangeDetectionStrategy, input, output, computed, signal, inject, effect, ElementRef } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OverlayModule, type ConnectedPosition } from '@angular/cdk/overlay';
import { toSignal } from '@angular/core/rxjs-interop';
import { ZagIcon } from '../icon/icon';
import { EntityVisualComponent } from '../entity-visual/entity-visual';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { FormattingService } from '../formatting/formatting.service';
import type { BreadcrumbItem, BreadcrumbDisplayItem, BreadcrumbDropdownOption } from './breadcrumb.types';
import {
  setMonth,
  getYear,
  getMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  addWeeks,
  addDays,
  format,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isToday,
  getISOWeek,
  getISOWeekYear,
} from 'date-fns';

interface MonthOption {
  label: string;
  value: number;
  isSelected: boolean;
  isCurrent: boolean;
}

interface CalendarDay {
  date: Date;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
}

interface CalendarWeek {
  weekNumber: number;
  weekStart: Date;
  days: CalendarDay[];
  isSelected: boolean;
  isCurrent: boolean;
}

/**
 * Breadcrumb navigation component.
 *
 * Displays hierarchical navigation with support for:
 * - Clickable parent items
 * - Sibling dropdown navigation
 * - Responsive truncation
 * - Accessibility via proper ARIA attributes
 * - Dropdowns rendered via CDK Overlay (not clipped by scroll containers)
 *
 * @example
 * ```html
 * <zag-breadcrumb [items]="breadcrumbs()" />
 * ```
 */
@Component({
  selector: 'zag-breadcrumb',
  imports: [TranslatePipe, ZagIcon, RouterModule, OverlayModule, EntityVisualComponent, TooltipDirective],
  template: `
    <nav [attr.aria-label]="'breadcrumb.navAriaLabel' | translate">
      <ol class="breadcrumb__list">
        @for (item of displayItems(); track $index; let last = $last) {
          <li
            class="breadcrumb__item"
            [class.breadcrumb__item--current]="item.isCurrent"
            [class.breadcrumb__item--fixed-width]="isFixedWidthItem(item, $index)"
          >
            @if (item.isTruncated) {
              <span class="breadcrumb__ellipsis" aria-hidden="true">...</span>
            } @else if (item.type === 'month-picker') {
              <!-- Month picker - opens dropdown with month grid -->
              <div class="breadcrumb__split-trigger" cdkOverlayOrigin #dropdownOrigin="cdkOverlayOrigin">
                <button
                  type="button"
                  class="breadcrumb__button breadcrumb__button--value"
                  [class.breadcrumb__button--open]="openDropdownIndex() === $index"
                  (click)="toggleDropdown($index, $event)"
                  [attr.aria-expanded]="openDropdownIndex() === $index"
                  aria-haspopup="dialog"
                >
                  <span zagIcon class="breadcrumb__icon" [zagIcon]="getBreadcrumbIcon(item)"></span>
                  <span class="breadcrumb__label">{{ item.label }}</span>
                </button>
                <div class="breadcrumb__step-group" role="group" [attr.aria-label]="item.label">
                  <button
                    type="button"
                    class="breadcrumb__step-btn"
                    (click)="onStep($index, -1, $event)"
                    [attr.aria-label]="getPrevAriaLabel(item)"
                    [zagTooltip]="getStepTooltip(item, $index, -1)"
                    tooltipPosition="below"
                  >
                    <span zagIcon="chevron_left"></span>
                  </button>
                  <button
                    type="button"
                    class="breadcrumb__step-btn"
                    (click)="onStep($index, 1, $event)"
                    [attr.aria-label]="getNextAriaLabel(item)"
                    [zagTooltip]="getStepTooltip(item, $index, 1)"
                    tooltipPosition="below"
                  >
                    <span zagIcon="chevron_right"></span>
                  </button>
                </div>
              </div>

              <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="dropdownOrigin"
                [cdkConnectedOverlayOpen]="openDropdownIndex() === $index && !!item.date"
                [cdkConnectedOverlayHasBackdrop]="true"
                [cdkConnectedOverlayBackdropClass]="'breadcrumb-overlay-backdrop'"
                (backdropClick)="closeDropdown()"
                [cdkConnectedOverlayPositions]="dropdownPositions"
              >
                <div class="breadcrumb__month-dropdown" role="dialog" [attr.aria-label]="'breadcrumb.selectMonth' | translate">
                  <div class="breadcrumb__month-header">
                    <button
                      type="button"
                      class="breadcrumb__month-nav"
                      (click)="onPrevYear(item, $event)"
                      [attr.aria-label]="'breadcrumb.prevYear' | translate"
                    >
                      <span zagIcon="chevron_left"></span>
                    </button>
                    <span class="breadcrumb__year-label">{{ getYear(item.date!) }}</span>
                    <button
                      type="button"
                      class="breadcrumb__month-nav"
                      (click)="onNextYear(item, $event)"
                      [attr.aria-label]="'breadcrumb.nextYear' | translate"
                    >
                      <span zagIcon="chevron_right"></span>
                    </button>
                  </div>
                  <div class="breadcrumb__month-grid">
                    @for (month of getMonthOptions(item.date!); track month.value) {
                      <button
                        type="button"
                        class="breadcrumb__month-option"
                        [class.breadcrumb__month-option--selected]="month.isSelected"
                        [class.breadcrumb__month-option--current]="month.isCurrent"
                        (click)="onMonthSelect(item, month.value, $event)"
                      >
                        {{ month.label }}
                      </button>
                    }
                  </div>
                </div>
              </ng-template>
            } @else if (item.type === 'date-picker') {
              <!-- Unified date picker - adapts between week and month selection -->
              <div class="breadcrumb__split-trigger" cdkOverlayOrigin #dropdownOrigin="cdkOverlayOrigin">
                <button
                  type="button"
                  class="breadcrumb__button breadcrumb__button--value"
                  [class.breadcrumb__button--open]="openDropdownIndex() === $index"
                  (click)="toggleDropdown($index, $event)"
                  [attr.aria-expanded]="openDropdownIndex() === $index"
                  aria-haspopup="dialog"
                >
                  <span zagIcon class="breadcrumb__icon" [zagIcon]="getBreadcrumbIcon(item)"></span>
                  <span class="breadcrumb__label">{{ getDatePickerLabel(item) }}</span>
                </button>
                <div class="breadcrumb__step-group" role="group" [attr.aria-label]="getDatePickerLabel(item)">
                  <button
                    type="button"
                    class="breadcrumb__step-btn"
                    (click)="onStep($index, -1, $event)"
                    [attr.aria-label]="getPrevAriaLabel(item)"
                    [zagTooltip]="getStepTooltip(item, $index, -1)"
                    tooltipPosition="below"
                  >
                    <span zagIcon="chevron_left"></span>
                  </button>
                  <button
                    type="button"
                    class="breadcrumb__step-btn"
                    (click)="onStep($index, 1, $event)"
                    [attr.aria-label]="getNextAriaLabel(item)"
                    [zagTooltip]="getStepTooltip(item, $index, 1)"
                    tooltipPosition="below"
                  >
                    <span zagIcon="chevron_right"></span>
                  </button>
                </div>
              </div>

              <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="dropdownOrigin"
                [cdkConnectedOverlayOpen]="openDropdownIndex() === $index && !!item.date"
                [cdkConnectedOverlayHasBackdrop]="true"
                [cdkConnectedOverlayBackdropClass]="'breadcrumb-overlay-backdrop'"
                (backdropClick)="closeDropdown()"
                [cdkConnectedOverlayPositions]="dropdownPositions"
              >
                @if (item.datePickerMode === 'month') {
                  <!-- Month mode: show month grid (same as month-picker) -->
                  <div class="breadcrumb__month-dropdown" role="dialog" [attr.aria-label]="'breadcrumb.selectMonth' | translate">
                    <div class="breadcrumb__month-header">
                      <button
                        type="button"
                        class="breadcrumb__month-nav"
                        (click)="onPrevYear(item, $event)"
                        [attr.aria-label]="'breadcrumb.prevYear' | translate"
                      >
                        <span zagIcon="chevron_left"></span>
                      </button>
                      <span class="breadcrumb__year-label">{{ getYear(item.date!) }}</span>
                      <button
                        type="button"
                        class="breadcrumb__month-nav"
                        (click)="onNextYear(item, $event)"
                        [attr.aria-label]="'breadcrumb.nextYear' | translate"
                      >
                        <span zagIcon="chevron_right"></span>
                      </button>
                    </div>
                    <div class="breadcrumb__month-grid">
                      @for (month of getMonthOptions(item.date!); track month.value) {
                        <button
                          type="button"
                          class="breadcrumb__month-option"
                          [class.breadcrumb__month-option--selected]="month.isSelected"
                          [class.breadcrumb__month-option--current]="month.isCurrent"
                          (click)="onDatePickerMonthSelect(item, month.value, $event)"
                        >
                          {{ month.label }}
                        </button>
                      }
                    </div>
                  </div>
                } @else {
                  <!-- Week/day mode: show calendar with week or single-day selection -->
                  <div
                    class="breadcrumb__calendar-dropdown"
                    role="dialog"
                    [attr.aria-label]="(item.datePickerMode === 'day' ? 'breadcrumb.selectDay' : 'breadcrumb.selectWeek') | translate"
                  >
                    <div class="breadcrumb__calendar-header">
                      <button
                        type="button"
                        class="breadcrumb__month-nav"
                        (click)="onCalendarPrevMonth(item, $event)"
                        [attr.aria-label]="'breadcrumb.prevMonth' | translate"
                      >
                        <span zagIcon="chevron_left"></span>
                      </button>
                      <span class="breadcrumb__calendar-month-label">{{ getCalendarMonthLabel(item.date!) }}</span>
                      <button
                        type="button"
                        class="breadcrumb__month-nav"
                        (click)="onCalendarNextMonth(item, $event)"
                        [attr.aria-label]="'breadcrumb.nextMonth' | translate"
                      >
                        <span zagIcon="chevron_right"></span>
                      </button>
                    </div>
                    <div class="breadcrumb__calendar-weekdays">
                      @for (day of getWeekdayLabels(); track day) {
                        <span class="breadcrumb__calendar-weekday">{{ day }}</span>
                      }
                    </div>
                    <div class="breadcrumb__calendar-weeks">
                      @for (week of getCalendarWeeks(item.date!); track week.weekNumber) {
                        @if (item.datePickerMode === 'day') {
                          <div class="breadcrumb__calendar-week-row breadcrumb__calendar-week-row--days">
                            @for (day of week.days; track day.date.toISOString()) {
                              <button
                                type="button"
                                class="breadcrumb__calendar-day breadcrumb__calendar-day--selectable"
                                [class.breadcrumb__calendar-day--other-month]="!day.isCurrentMonth"
                                [class.breadcrumb__calendar-day--today]="day.isToday"
                                [class.breadcrumb__calendar-day--selected]="day.isSelected"
                                (click)="onDaySelect(item, day.date, $event)"
                                [attr.aria-pressed]="day.isSelected"
                              >
                                {{ day.dayOfMonth }}
                              </button>
                            }
                          </div>
                        } @else {
                          <button
                            type="button"
                            class="breadcrumb__calendar-week-row"
                            [class.breadcrumb__calendar-week-row--selected]="week.isSelected"
                            [class.breadcrumb__calendar-week-row--current]="week.isCurrent"
                            (click)="onWeekSelect(item, week.weekStart, $event)"
                            [attr.aria-label]="'breadcrumb.weekAriaLabel' | translate: { week: week.weekNumber }"
                          >
                            @for (day of week.days; track day.date.toISOString()) {
                              <span
                                class="breadcrumb__calendar-day"
                                [class.breadcrumb__calendar-day--other-month]="!day.isCurrentMonth"
                                [class.breadcrumb__calendar-day--today]="day.isToday"
                              >
                                {{ day.dayOfMonth }}
                              </span>
                            }
                          </button>
                        }
                      }
                    </div>
                  </div>
                }
              </ng-template>
            } @else if (item.type === 'dropdown') {
              <!-- Interactive dropdown for value selection -->
              <div class="breadcrumb__split-trigger" cdkOverlayOrigin #dropdownOrigin="cdkOverlayOrigin">
                <button
                  type="button"
                  class="breadcrumb__button breadcrumb__button--value"
                  [class.breadcrumb__button--open]="openDropdownIndex() === $index"
                  [class.breadcrumb__button--placeholder]="isShowingDropdownPlaceholder(item)"
                  (click)="toggleDropdown($index, $event)"
                  [attr.aria-expanded]="openDropdownIndex() === $index"
                  aria-haspopup="listbox"
                >
                  <zag-entity-visual
                    [icon]="getDropdownIcon(item)"
                    [color]="getDropdownColor(item)"
                    [imageUrl]="getDropdownImageUrl(item)"
                    class="breadcrumb__icon"
                  />
                  <span class="breadcrumb__label">{{ getDropdownLabel(item) | translate }}</span>
                </button>
                <div class="breadcrumb__step-group" role="group" [attr.aria-label]="getDropdownLabel(item) | translate">
                  <button
                    type="button"
                    class="breadcrumb__step-btn"
                    (click)="onStep($index, -1, $event)"
                    [attr.aria-label]="getPrevAriaLabel(item)"
                    [zagTooltip]="getStepTooltip(item, $index, -1)"
                    tooltipPosition="below"
                  >
                    <span zagIcon="chevron_left"></span>
                  </button>
                  <button
                    type="button"
                    class="breadcrumb__step-btn"
                    (click)="onStep($index, 1, $event)"
                    [attr.aria-label]="getNextAriaLabel(item)"
                    [zagTooltip]="getStepTooltip(item, $index, 1)"
                    tooltipPosition="below"
                  >
                    <span zagIcon="chevron_right"></span>
                  </button>
                </div>
              </div>

              <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="dropdownOrigin"
                [cdkConnectedOverlayOpen]="openDropdownIndex() === $index && !!item.options"
                [cdkConnectedOverlayHasBackdrop]="true"
                [cdkConnectedOverlayBackdropClass]="'breadcrumb-overlay-backdrop'"
                (backdropClick)="closeDropdown()"
                [cdkConnectedOverlayPositions]="dropdownPositions"
              >
                <div class="breadcrumb__dropdown" role="listbox">
                  @for (option of item.options; track option.value) {
                    @if (option.isAction) {
                      <div class="breadcrumb__dropdown-separator" role="separator"></div>
                    }
                    <button
                      type="button"
                      class="breadcrumb__dropdown-item"
                      [class.breadcrumb__dropdown-item--selected]="option.value === item.value"
                      [class.breadcrumb__dropdown-item--action]="!!option.isAction"
                      role="option"
                      [attr.aria-selected]="option.value === item.value"
                      (click)="onDropdownSelect(item, option, $event)"
                    >
                      <zag-entity-visual
                        [icon]="getOptionIcon(option)"
                        [color]="option.color"
                        [imageUrl]="option.imageUrl"
                        class="breadcrumb__dropdown-icon"
                      />
                      <span class="breadcrumb__label">{{ option.label }}</span>
                    </button>
                  }
                </div>
              </ng-template>
            } @else if (item.isCurrent) {
              <!-- Current item (active page) - whole item clickable if has siblings -->
              @if (shouldShowAsDropdown($index)) {
                <div class="breadcrumb__split-trigger" cdkOverlayOrigin #dropdownOrigin="cdkOverlayOrigin">
                  <button
                    type="button"
                    class="breadcrumb__button breadcrumb__button--value"
                    [class.breadcrumb__button--open]="openDropdownIndex() === $index"
                    (click)="toggleDropdown($index, $event)"
                    [attr.aria-expanded]="openDropdownIndex() === $index"
                    aria-haspopup="listbox"
                    aria-current="page"
                  >
                    <zag-entity-visual
                      [icon]="getBreadcrumbIcon(item)"
                      [color]="item.color"
                      [imageUrl]="item.imageUrl"
                      class="breadcrumb__icon"
                    />
                    <span class="breadcrumb__label">{{ item.label | translate }}</span>
                  </button>
                  <div class="breadcrumb__step-group" role="group" [attr.aria-label]="item.label | translate">
                    <button
                      type="button"
                      class="breadcrumb__step-btn"
                      (click)="onStep($index, -1, $event)"
                      [attr.aria-label]="getPrevAriaLabel(item)"
                      [zagTooltip]="getStepTooltip(item, $index, -1)"
                      tooltipPosition="below"
                    >
                      <span zagIcon="chevron_left"></span>
                    </button>
                    <button
                      type="button"
                      class="breadcrumb__step-btn"
                      (click)="onStep($index, 1, $event)"
                      [attr.aria-label]="getNextAriaLabel(item)"
                      [zagTooltip]="getStepTooltip(item, $index, 1)"
                      tooltipPosition="below"
                    >
                      <span zagIcon="chevron_right"></span>
                    </button>
                  </div>
                </div>

                <ng-template
                  cdkConnectedOverlay
                  [cdkConnectedOverlayOrigin]="dropdownOrigin"
                  [cdkConnectedOverlayOpen]="openDropdownIndex() === $index"
                  [cdkConnectedOverlayHasBackdrop]="true"
                  [cdkConnectedOverlayBackdropClass]="'breadcrumb-overlay-backdrop'"
                  (backdropClick)="closeDropdown()"
                  [cdkConnectedOverlayPositions]="dropdownPositions"
                >
                  <div class="breadcrumb__dropdown" role="listbox">
                    @for (sibling of getSiblingsForOverlay($index); track sibling.sectionLabel ?? sibling.label) {
                      @if (sibling.separator) {
                        <div class="breadcrumb__dropdown-section" role="presentation">
                          <span class="breadcrumb__dropdown-section-label">{{ sibling.sectionLabel | translate }}</span>
                        </div>
                      } @else {
                        <button
                          type="button"
                          class="breadcrumb__dropdown-item"
                          role="option"
                          aria-selected="false"
                          (click)="onSiblingClick(sibling)"
                        >
                          <zag-entity-visual
                            [icon]="getBreadcrumbIcon(sibling)"
                            [color]="sibling.color"
                            [imageUrl]="sibling.imageUrl"
                            class="breadcrumb__dropdown-icon"
                          />
                          <span class="breadcrumb__label">{{ sibling.label | translate }}</span>
                        </button>
                      }
                    }
                  </div>
                </ng-template>
              } @else {
                <span class="breadcrumb__text" aria-current="page">
                  <span class="breadcrumb__label">{{ item.label | translate }}</span>
                </span>
              }
            } @else if (!item.route) {
              <!-- Inactive breadcrumb (no link, just text) -->
              <span class="breadcrumb__text breadcrumb__text--inactive">
                <span class="breadcrumb__label">{{ item.label | translate }}</span>
              </span>
            } @else {
              <!-- Clickable breadcrumb - always a simple link for parent items -->
              <a class="breadcrumb__link" [routerLink]="item.route" [queryParams]="item.queryParams" (click)="onItemClick(item, $event)">
                <span class="breadcrumb__label">{{ item.label | translate }}</span>
              </a>
            }

            @if (!last) {
              <span class="breadcrumb__separator" aria-hidden="true">·</span>
            }
          </li>
        }
      </ol>
    </nav>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        min-height: 36px;
      }

      nav {
        display: contents;
      }

      .breadcrumb__list {
        display: inline-flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 2px;
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .breadcrumb__item {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        position: relative;
        flex: 0 0 auto;
        width: auto;
        height: var(--breadcrumb-item-height);
        min-width: 0;
      }

      .breadcrumb__item--fixed-width {
        flex: 0 0 var(--breadcrumb-item-width);
        width: var(--breadcrumb-item-width);
      }

      .breadcrumb__link,
      .breadcrumb__text {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-xs);
        height: var(--breadcrumb-item-height);
        padding: 6px 10px;
        border-radius: var(--border-radius-sm);
        box-sizing: border-box;
        font-size: var(--font-size-body);
        line-height: 1.4;
        text-decoration: none;
        background: transparent;
        white-space: nowrap;
        transition:
          background-color var(--transition-duration-fast),
          color var(--transition-duration-fast);
      }

      .breadcrumb__link {
        color: var(--text-color-secondary);
        cursor: pointer;

        &:hover {
          background-color: var(--surface-color-hover);
          color: var(--text-color-primary);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 1px;
        }
      }

      .breadcrumb__text {
        color: var(--text-color-primary);
        font-weight: 600;
      }

      .breadcrumb__text--inactive {
        color: var(--text-color-secondary);
        font-weight: 400;
      }

      /* Whole-item clickable button (for items with dropdowns) */
      .breadcrumb__button {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-xs);
        height: var(--breadcrumb-item-height);
        padding: 6px 10px;
        border: none;
        border-radius: var(--border-radius-sm);
        box-sizing: border-box;
        font-family: inherit;
        font-size: var(--font-size-body);
        font-weight: 600;
        line-height: 1.4;
        color: var(--text-color-primary);
        background: transparent;
        cursor: pointer;
        white-space: nowrap;
        transition:
          background-color var(--transition-duration-fast),
          color var(--transition-duration-fast);

        &:hover {
          background-color: var(--surface-color-hover);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 1px;
        }
      }

      .breadcrumb__button--link {
        color: var(--text-color-secondary);
        font-weight: 400;

        &:hover {
          color: var(--text-color-primary);
        }
      }

      .breadcrumb__label {
        display: block;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-left: var(--spacing-xs);
        margin-right: auto;
      }

      .breadcrumb__chevron {
        font-size: 18px;
        line-height: 1;
        transition: transform var(--transition-duration-fast);
      }

      .breadcrumb__button--open .breadcrumb__chevron {
        transform: rotate(180deg);
      }

      .breadcrumb__icon {
        font-size: 18px;
        line-height: 1;
      }

      .breadcrumb__separator {
        display: inline-flex;
        align-items: center;
        color: var(--text-color-tertiary, var(--text-color-secondary));
        font-size: 20px;
        line-height: 1;
        opacity: 0.5;
        margin: 0 2px;
      }

      .breadcrumb__ellipsis {
        display: inline-flex;
        align-items: center;
        color: var(--text-color-secondary);
        padding: 6px 8px;
        font-size: var(--font-size-body);
      }

      /* Dropdown panel (rendered in CDK overlay, NOT position: absolute) */
      .breadcrumb__dropdown {
        min-width: 240px;
        width: max-content;
        max-width: 400px;
        max-height: 320px;
        overflow-y: auto;
        overscroll-behavior: contain;
        background: var(--background-color-elevated, var(--surface-color-primary));
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        box-shadow: var(--shadow-lg);
        z-index: var(--z-index-dropdown, 1000);
      }

      .breadcrumb__dropdown-item {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        width: 100%;
        min-width: 0;
        padding: var(--spacing-sm) var(--spacing-md);
        box-sizing: border-box;
        background: transparent;
        border: none;
        color: var(--text-color-primary);
        font-family: inherit;
        font-size: var(--font-size-body);
        text-align: left;
        text-decoration: none;
        cursor: pointer;
        transition: background-color var(--transition-duration-fast);

        &:hover {
          background-color: var(--surface-color-hover);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: -2px;
        }
      }

      .breadcrumb__dropdown-icon {
        font-size: 18px;
        line-height: 1;
        color: var(--text-color-secondary);
      }

      .breadcrumb__dropdown-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--spacing-md);
      }

      .breadcrumb__dropdown-item--selected {
        background-color: var(--surface-color-hover);
        font-weight: 500;
      }

      .breadcrumb__dropdown-separator {
        height: 1px;
        background: var(--border-color-default);
        margin: var(--spacing-xs) 0;
      }

      .breadcrumb__dropdown-section {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-xs) var(--spacing-sm);
        margin-top: var(--spacing-xs);

        &::before {
          content: '';
          flex: 0 0 var(--spacing-sm);
          height: 1px;
          background: var(--border-color-default);
        }

        &::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--border-color-default);
        }
      }

      .breadcrumb__dropdown-section-label {
        flex-shrink: 0;
        font-size: var(--font-size-xs, 11px);
        font-weight: var(--font-weight-medium);
        color: var(--text-color-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        white-space: nowrap;
      }

      .breadcrumb__dropdown-item--action {
        color: var(--color-primary);
        font-weight: 500;
      }

      .breadcrumb__button--placeholder {
        color: var(--text-color-tertiary);
        font-style: italic;
      }

      /* Entity visual in breadcrumb triggers (current item button, link, text) */
      .breadcrumb__icon zag-entity-visual,
      zag-entity-visual.breadcrumb__icon {
        width: var(--entity-avatar-size-list);
        height: var(--entity-avatar-size-list);
        flex-shrink: 0;
        border-radius: 50%;
        overflow: hidden;
      }

      /* Entity visual in dropdown panel option rows */
      zag-entity-visual.breadcrumb__dropdown-icon {
        width: var(--entity-avatar-size-list);
        height: var(--entity-avatar-size-list);
        flex-shrink: 0;
        border-radius: 50%;
        overflow: hidden;
      }

      /* Month picker dropdown (rendered in CDK overlay) */
      .breadcrumb__month-dropdown {
        width: 280px;
        background: var(--background-color-elevated, var(--surface-color-primary));
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        box-shadow: var(--shadow-lg);
        z-index: var(--z-index-dropdown, 1000);
        padding: var(--spacing-sm);
      }

      .breadcrumb__month-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--spacing-xs) 0;
        margin-bottom: var(--spacing-sm);
        border-bottom: 1px solid var(--border-color-default);
      }

      .breadcrumb__year-label {
        font-size: var(--font-size-body);
        font-weight: 600;
        color: var(--text-color-primary);
      }

      .breadcrumb__month-nav {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border: none;
        border-radius: var(--border-radius-sm);
        background: transparent;
        color: var(--text-color-secondary);
        cursor: pointer;
        transition:
          background-color var(--transition-duration-fast),
          color var(--transition-duration-fast);

        &:hover {
          background-color: var(--surface-color-hover);
          color: var(--text-color-primary);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 1px;
        }
      }

      .breadcrumb__month-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--spacing-xs);
      }

      .breadcrumb__month-option {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--spacing-sm) var(--spacing-xs);
        border: none;
        border-radius: var(--border-radius-sm);
        background: transparent;
        color: var(--text-color-primary);
        font-family: inherit;
        font-size: var(--font-size-sm);
        cursor: pointer;
        transition: background-color var(--transition-duration-fast);

        &:hover {
          background-color: var(--surface-color-hover);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: -2px;
        }
      }

      .breadcrumb__month-option--selected {
        background-color: var(--color-primary);
        color: var(--color-on-primary, #fff);

        &:hover {
          background-color: var(--color-primary-hover, var(--color-primary));
        }
      }

      .breadcrumb__month-option--current:not(.breadcrumb__month-option--selected) {
        font-weight: 600;
        box-shadow: inset 0 0 0 1px var(--color-primary);
      }

      /* Calendar (week picker) dropdown (rendered in CDK overlay) */
      .breadcrumb__calendar-dropdown {
        width: 320px;
        background: var(--background-color-elevated, var(--surface-color-primary));
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        box-shadow: var(--shadow-lg);
        z-index: var(--z-index-dropdown, 1000);
        padding: var(--spacing-sm);
      }

      .breadcrumb__calendar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--spacing-xs) 0;
        margin-bottom: var(--spacing-sm);
        border-bottom: 1px solid var(--border-color-default);
      }

      .breadcrumb__calendar-month-label {
        font-size: var(--font-size-body);
        font-weight: 600;
        color: var(--text-color-primary);
      }

      .breadcrumb__calendar-weekdays {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        margin-bottom: var(--spacing-xs);
      }

      .breadcrumb__calendar-weekday {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--spacing-xs);
        font-size: var(--font-size-xs);
        font-weight: 600;
        color: var(--text-color-secondary);
        text-transform: uppercase;
      }

      .breadcrumb__calendar-weeks {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .breadcrumb__calendar-week-row {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        padding: var(--spacing-xs);
        border: none;
        border-radius: var(--border-radius-sm);
        background: transparent;
        cursor: pointer;
        transition: background-color var(--transition-duration-fast);

        &:hover {
          background-color: var(--surface-color-hover);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: -2px;
        }
      }

      .breadcrumb__calendar-week-row--selected {
        background-color: var(--color-primary);

        &:hover {
          background-color: var(--color-primary-hover, var(--color-primary));
        }

        .breadcrumb__calendar-day {
          color: var(--color-on-primary, #fff);
        }

        .breadcrumb__calendar-day--other-month {
          color: var(--color-on-primary, #fff);
          opacity: 0.6;
        }
      }

      .breadcrumb__calendar-week-row--current:not(.breadcrumb__calendar-week-row--selected) {
        box-shadow: inset 0 0 0 1px var(--color-primary);
      }

      .breadcrumb__calendar-day {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px;
        font-size: var(--font-size-sm);
        color: var(--text-color-primary);
      }

      .breadcrumb__calendar-day--other-month {
        color: var(--text-color-tertiary, var(--text-color-secondary));
        opacity: 0.5;
      }

      .breadcrumb__calendar-day--today {
        font-weight: 700;
        text-decoration: underline;
      }

      .breadcrumb__calendar-week-row--days {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        border: none;
        background: transparent;
        padding: 0;
      }

      .breadcrumb__calendar-day--selectable {
        border: none;
        background: transparent;
        border-radius: var(--border-radius-md);
        cursor: pointer;

        &:hover {
          background-color: var(--surface-color-hover);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: -2px;
        }
      }

      .breadcrumb__calendar-day--selected {
        background-color: var(--color-primary);
        color: var(--color-on-primary, #fff);

        &:hover {
          background-color: var(--color-primary-hover, var(--color-primary));
        }
      }

      /* Value stays first while step controls remain pinned to the right edge. */
      .breadcrumb__split-trigger {
        display: inline-flex;
        align-items: stretch;
        width: 100%;
        min-width: 0;
      }

      .breadcrumb__split-trigger .breadcrumb__button--value {
        flex: 1 1 auto;
        width: auto;
        min-width: 0;
        overflow: hidden;
      }

      .breadcrumb__step-group {
        display: inline-flex;
        align-items: stretch;
        flex: 0 0 auto;
        margin-left: auto;
      }

      .breadcrumb__step-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 var(--icon-size-main);
        width: var(--icon-size-main);
        min-width: var(--icon-size-main);
        padding: 0;
        border: none;
        border-radius: 0;
        background: transparent;
        cursor: pointer;
        color: var(--text-color-secondary);
        font-size: var(--icon-size-secondary);
        transition:
          background-color var(--transition-duration-fast),
          color var(--transition-duration-fast);

        &:hover {
          background-color: var(--surface-color-hover);
          color: var(--text-color-primary);
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 1px;
        }
      }

      .breadcrumb__step-group .breadcrumb__step-btn:last-child {
        border-top-right-radius: var(--border-radius-sm);
        border-bottom-right-radius: var(--border-radius-sm);
      }
      .breadcrumb__step-group .breadcrumb__step-btn:first-child {
        border-top-left-radius: var(--border-radius-sm);
        border-bottom-left-radius: var(--border-radius-sm);
      }

      .spinner {
        width: 18px;
        height: 18px;
        border: 2px solid var(--border-color-default);
        border-top-color: var(--color-primary);
        border-radius: 50%;
        animation: spin var(--animation-spin-duration, 1s) linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-breadcrumb',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class BreadcrumbComponent {
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  private readonly translate = inject(TranslateService);
  private readonly formatting = inject(FormattingService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  /** Breadcrumb items to display */
  readonly items = input.required<BreadcrumbItem[]>();

  /** Maximum items to show before truncating (0 = no limit) */
  readonly maxItems = input<number>(0);

  /** Emits when an item is clicked */
  readonly itemClicked = output<BreadcrumbItem>();

  // Track which dropdown is open
  protected readonly openDropdownIndex = signal<number | null>(null);

  // Track loaded siblings by cache key (label + route combination)
  // This ensures cache is per-item, not per-index
  protected readonly loadedSiblings = signal<Map<string, BreadcrumbItem[]>>(new Map());

  /** CDK Overlay positions for dropdowns (below the trigger) */
  protected readonly dropdownPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  constructor() {
    // Preload siblings only for the current item (last item in breadcrumb)
    // This allows us to know if there are actually siblings before rendering dropdown UI
    effect(() => {
      this.currentLang();
      const items = this.items();
      // Only the current (last) item can have a dropdown
      const currentItem = items.find(item => item.isCurrent);
      if (currentItem?.siblingsLoader) {
        const cacheKey = this.getCacheKey(currentItem);
        if (!this.loadedSiblings().has(cacheKey)) {
          // Mark as loading (empty array)
          this.loadedSiblings.update(map => {
            const newMap = new Map(map);
            newMap.set(cacheKey, []);
            return newMap;
          });

          currentItem.siblingsLoader().subscribe({
            next: siblings => {
              this.loadedSiblings.update(map => {
                const newMap = new Map(map);
                newMap.set(cacheKey, siblings);
                return newMap;
              });
            },
            // On error: leave the empty-array placeholder in place. The effect
            // depends on loadedSiblings, so deleting the key here would re-run
            // the effect, which would re-subscribe the loader, which would
            // error again — an infinite loop on any transient failure. Keeping
            // the empty array means "no siblings" until the breadcrumb item
            // changes (which produces a different cache key).
            error: () => {
              // Intentionally no-op. See comment above.
            },
          });
        }
      }
    });
  }

  protected getBreadcrumbIcon(item: BreadcrumbItem): string {
    if (item.icon) return item.icon;
    if (item.type === 'month-picker' || (item.type === 'date-picker' && item.datePickerMode === 'month')) {
      return 'calendar_month';
    }
    if (item.type === 'date-picker' && item.datePickerMode === 'week') return 'calendar_view_week';
    if (item.type === 'date-picker') return 'event';
    return 'category';
  }

  protected getOptionIcon(option: BreadcrumbDropdownOption): string {
    return option.icon || 'category';
  }

  protected getDropdownIcon(item: BreadcrumbItem): string {
    return this.getDropdownActiveOption(item)?.icon || item.icon || this.getBreadcrumbIcon(item);
  }

  protected getDropdownColor(item: BreadcrumbItem): string | null | undefined {
    return this.getDropdownActiveOption(item)?.color ?? item.color;
  }

  protected getDropdownImageUrl(item: BreadcrumbItem): string | null | undefined {
    return this.getDropdownActiveOption(item)?.imageUrl ?? item.imageUrl;
  }

  /**
   * Generate a cache key for a breadcrumb item.
   * Uses label and route to uniquely identify the item.
   */
  private getCacheKey(item: BreadcrumbItem): string {
    const routeStr = item.route?.join('/') ?? '';
    return `${this.currentLang().lang}:${item.label}:${routeStr}`;
  }

  // Computed: items to display (with truncation if needed)
  protected readonly displayItems = computed((): BreadcrumbDisplayItem[] => {
    const allItems = this.items();
    const max = this.maxItems();
    const indexedItems = allItems.map((item, sourceIndex) => ({ ...item, sourceIndex }));

    if (max === 0 || allItems.length <= max) {
      return indexedItems;
    }

    // Keep first, last two, and show ellipsis in middle
    const first = indexedItems.slice(0, 1);
    const last = indexedItems.slice(-2);

    return [...first, { label: '...', isTruncated: true }, ...last];
  });

  private getSourceItem(displayIndex: number): BreadcrumbItem | undefined {
    const displayItem = this.displayItems()[displayIndex];
    if (!displayItem || displayItem.isTruncated || displayItem.sourceIndex === undefined) return undefined;
    return this.items()[displayItem.sourceIndex];
  }

  protected isFixedWidthItem(item: BreadcrumbDisplayItem, displayIndex: number): boolean {
    if (item.type === 'dropdown' || item.type === 'month-picker' || item.type === 'date-picker') return true;
    return item.isCurrent === true && this.shouldShowAsDropdown(displayIndex);
  }

  protected onEscape(): void {
    this.closeDropdown();
  }

  protected onItemClick(item: BreadcrumbItem, event: Event): void {
    if (item.isCurrent || !item.route) {
      event.preventDefault();
      return;
    }

    this.itemClicked.emit(item);
    // Let RouterLink handle navigation
  }

  protected toggleDropdown(index: number, event: Event): void {
    event.stopPropagation();

    if (this.openDropdownIndex() === index) {
      this.openDropdownIndex.set(null);
      return;
    }

    this.openDropdownIndex.set(index);
  }

  protected getSiblings(index: number): BreadcrumbItem[] | undefined {
    const item = this.getSourceItem(index);
    if (!item) return undefined;
    const cacheKey = this.getCacheKey(item);
    return this.loadedSiblings().get(cacheKey);
  }

  /** Siblings visible in the overlay — excludes the current item (marked isCurrent: true by the loader). */
  protected getSiblingsForOverlay(index: number): BreadcrumbItem[] | undefined {
    return this.getSiblings(index)?.filter(s => !s.isCurrent);
  }

  /**
   * Check if an item should show as a dropdown.
   * Returns true only if siblings have been loaded AND there is at least one non-current sibling.
   */
  protected shouldShowAsDropdown(index: number): boolean {
    const item = this.getSourceItem(index);
    if (!item?.hasSiblings) return false;

    const siblings = this.getSiblingsForOverlay(index);
    return Array.isArray(siblings) && siblings.length > 0;
  }

  /** Step the date or sibling list by +1 / -1. */
  protected onStep(index: number, direction: -1 | 1, event: Event): void {
    event.stopPropagation();
    const displayItem = this.displayItems()[index];
    if (!displayItem || displayItem.isTruncated) return;

    const item = this.getSourceItem(index);
    if (!item) return;

    if (item.type === 'dropdown') {
      this.stepDropdown(item, direction);
      return;
    }

    if ((item.type === 'date-picker' || item.type === 'month-picker') && item.date) {
      this.stepDate(item, item.date, direction);
      return;
    }

    if (item.isCurrent) {
      this.stepSibling(index, direction);
    }
  }

  private stepDropdown(item: BreadcrumbDisplayItem, direction: -1 | 1): void {
    if (!item.options || !item.value) return;
    const navigableOptions = item.options.filter(o => !o.isAction);
    if (navigableOptions.length < 2) return;
    const activeIndex = navigableOptions.findIndex(o => o.value === item.value);
    if (activeIndex === -1) return;
    const nextIndex = (activeIndex + direction + navigableOptions.length) % navigableOptions.length;
    this.openDropdownIndex.set(null);
    item.onSelect?.(navigableOptions[nextIndex].value);
  }

  private stepDate(item: BreadcrumbDisplayItem, date: Date, direction: -1 | 1): void {
    if (item.type === 'month-picker' || item.datePickerMode === 'month') {
      item.onDateChange?.(addMonths(date, direction));
    } else if (item.datePickerMode === 'week') {
      item.onWeekSelect?.(startOfWeek(addWeeks(date, direction), { weekStartsOn: 1 }));
    } else {
      item.onDaySelect?.(addDays(date, direction));
    }
  }

  private stepSibling(index: number, direction: -1 | 1): void {
    const siblings = this.getSiblings(index);
    if (!siblings || siblings.length < 2) return;
    const activeIndex = siblings.findIndex(s => s.isCurrent);
    if (activeIndex === -1) return;
    const nextIndex = (activeIndex + direction + siblings.length) % siblings.length;
    this.onSiblingClick(siblings[nextIndex]);
  }

  protected getPrevAriaLabel(item: BreadcrumbItem): string {
    if (item.type === 'date-picker') {
      if (item.datePickerMode === 'day') return this.translate.instant('breadcrumb.prevDay');
      if (item.datePickerMode === 'week') return this.translate.instant('breadcrumb.prevWeek');
    }
    if (item.type === 'date-picker' || item.type === 'month-picker' || item.type === 'dropdown') {
      return this.translate.instant('breadcrumb.prevOption');
    }
    return this.translate.instant('breadcrumb.prevItem');
  }

  protected getNextAriaLabel(item: BreadcrumbItem): string {
    if (item.type === 'date-picker') {
      if (item.datePickerMode === 'day') return this.translate.instant('breadcrumb.nextDay');
      if (item.datePickerMode === 'week') return this.translate.instant('breadcrumb.nextWeek');
    }
    if (item.type === 'date-picker' || item.type === 'month-picker' || item.type === 'dropdown') {
      return this.translate.instant('breadcrumb.nextOption');
    }
    return this.translate.instant('breadcrumb.nextItem');
  }

  protected onSiblingClick(sibling: BreadcrumbItem): void {
    this.openDropdownIndex.set(null);

    if (sibling.route) {
      this.router.navigate(sibling.route, { queryParams: sibling.queryParams });
    }
  }

  protected closeDropdown(): void {
    this.openDropdownIndex.set(null);
    this.yearOffset = 0; // Reset year offset when closing
    this.calendarMonthOffset = 0; // Reset calendar month offset when closing
  }

  // --- Month picker handlers ---

  /** Temporary year offset for month picker navigation (doesn't change selection until month is clicked) */
  private yearOffset = 0;

  /** Temporary month offset for calendar (week picker) navigation */
  private calendarMonthOffset = 0;

  protected getYear(date: Date): number {
    return getYear(date) + this.yearOffset;
  }

  protected getMonthOptions(date: Date): MonthOption[] {
    const currentDate = new Date();
    const selectedMonth = getMonth(date);
    const selectedYear = getYear(date);
    const displayYear = selectedYear + this.yearOffset;

    const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

    return monthKeys.map((key, index) => ({
      label: this.translate.instant(`breadcrumb.months.${key}`),
      value: index,
      isSelected: index === selectedMonth && this.yearOffset === 0,
      isCurrent: index === getMonth(currentDate) && displayYear === getYear(currentDate),
    }));
  }

  protected onPrevYear(item: BreadcrumbItem, event: Event): void {
    event.stopPropagation();
    this.yearOffset--;
  }

  protected onNextYear(item: BreadcrumbItem, event: Event): void {
    event.stopPropagation();
    this.yearOffset++;
  }

  /** Common handler for month selection */
  private handleMonthSelection(item: BreadcrumbItem, monthIndex: number, event: Event): void {
    event.stopPropagation();
    this.openDropdownIndex.set(null);

    if (item.date && item.onDateChange) {
      const baseYear = getYear(item.date) + this.yearOffset;
      const newDate = setMonth(new Date(baseYear, 0, 1), monthIndex);
      this.yearOffset = 0;
      item.onDateChange(newDate);
    }
  }

  protected onMonthSelect(item: BreadcrumbItem, monthIndex: number, event: Event): void {
    this.handleMonthSelection(item, monthIndex, event);
  }

  // --- Date picker (unified) handlers ---

  /** Get the display label for a date-picker item (computed from date) */
  protected getDatePickerLabel(item: BreadcrumbItem): string {
    if (!item.date) return item.label ?? '';

    // In day mode, show the locale-formatted calendar date. The item's date
    // is a timezone-less calendar day — pin the formatting to UTC so the
    // tenant timezone cannot shift it to the neighbouring day.
    if (item.datePickerMode === 'day') {
      const day = item.date;
      const utcDay = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()));
      return this.formatting.formatDate(utcDay, { timeZone: 'UTC' });
    }

    // In week mode, show "Week X, YYYY" format
    if (item.datePickerMode === 'week') {
      const weekNumber = getISOWeek(item.date);
      const year = getISOWeekYear(item.date);
      return this.translate.instant('breadcrumb.weekLabel', {
        week: weekNumber,
        year: year,
      });
    }

    // In month mode, show "Month YYYY" format
    const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const monthKey = monthKeys[getMonth(item.date)];
    const monthName = this.translate.instant(`breadcrumb.monthsFull.${monthKey}`);
    return `${monthName} ${getYear(item.date)}`;
  }

  /** Get weekday labels for calendar header (translated) */
  protected getWeekdayLabels(): string[] {
    const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    return dayKeys.map(key => this.translate.instant(`breadcrumb.daysShort.${key}`));
  }

  /** Handle month selection in date-picker (month mode) */
  protected onDatePickerMonthSelect(item: BreadcrumbItem, monthIndex: number, event: Event): void {
    this.handleMonthSelection(item, monthIndex, event);
  }

  /** Get the month/year label for the calendar header */
  protected getCalendarMonthLabel(date: Date): string {
    const displayDate = this.calendarMonthOffset === 0 ? date : addMonths(date, this.calendarMonthOffset);
    const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const monthKey = monthKeys[getMonth(displayDate)];
    const monthName = this.translate.instant(`breadcrumb.monthsFull.${monthKey}`);
    return `${monthName} ${getYear(displayDate)}`;
  }

  /** Get calendar weeks for the week picker */
  protected getCalendarWeeks(selectedDate: Date): CalendarWeek[] {
    const displayDate = this.calendarMonthOffset === 0 ? selectedDate : addMonths(selectedDate, this.calendarMonthOffset);

    // Get the first day to display (Monday of the week containing the 1st of the month)
    const monthStart = new Date(getYear(displayDate), getMonth(displayDate), 1);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });

    // Get the last day to display (Sunday of the week containing the last day of the month)
    const monthEnd = new Date(getYear(displayDate), getMonth(displayDate) + 1, 0);
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const today = new Date();
    const weeks: CalendarWeek[] = [];
    let currentWeekStart = calendarStart;

    while (currentWeekStart <= calendarEnd) {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const days = eachDayOfInterval({ start: currentWeekStart, end: weekEnd });

      weeks.push({
        weekNumber: getISOWeek(currentWeekStart),
        weekStart: currentWeekStart,
        days: days.map(day => ({
          date: day,
          dayOfMonth: day.getDate(),
          isCurrentMonth: isSameMonth(day, displayDate),
          isToday: isToday(day),
          isSelected: isSameDay(day, selectedDate),
        })),
        isSelected: this.calendarMonthOffset === 0 && isSameWeek(currentWeekStart, selectedDate, { weekStartsOn: 1 }),
        isCurrent: isSameWeek(currentWeekStart, today, { weekStartsOn: 1 }),
      });

      // Move to next week
      currentWeekStart = new Date(currentWeekStart);
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }

    return weeks;
  }

  /** Navigate to previous month in calendar */
  protected onCalendarPrevMonth(item: BreadcrumbItem, event: Event): void {
    event.stopPropagation();
    this.calendarMonthOffset--;
  }

  /** Navigate to next month in calendar */
  protected onCalendarNextMonth(item: BreadcrumbItem, event: Event): void {
    event.stopPropagation();
    this.calendarMonthOffset++;
  }

  /** Handle week selection in calendar */
  protected onWeekSelect(item: BreadcrumbItem, weekStart: Date, event: Event): void {
    event.stopPropagation();
    this.openDropdownIndex.set(null);
    this.calendarMonthOffset = 0;

    if (item.onWeekSelect) {
      item.onWeekSelect(weekStart);
    }
  }

  /** Handle single-day selection in calendar (day mode) */
  protected onDaySelect(item: BreadcrumbItem, day: Date, event: Event): void {
    event.stopPropagation();
    this.openDropdownIndex.set(null);
    this.calendarMonthOffset = 0;

    if (item.onDaySelect) {
      item.onDaySelect(day);
    }
  }

  // --- Dropdown handlers ---

  protected getDropdownLabel(item: BreadcrumbItem): string {
    if (item.value && item.options) {
      const selected = item.options.find(opt => opt.value === item.value);
      if (selected) {
        return selected.label;
      }
    }
    return item.placeholder ?? item.label ?? '';
  }

  protected getDropdownActiveOption(item: BreadcrumbItem): BreadcrumbDropdownOption | null {
    if (!item.value || !item.options) return null;
    return item.options.find(opt => opt.value === item.value) ?? null;
  }

  protected getStepTooltip(item: BreadcrumbItem, index: number, direction: -1 | 1): string {
    if (item.type === 'dropdown') {
      if (!item.options || !item.value) return '';
      const navigable = item.options.filter(o => !o.isAction);
      const activeIndex = navigable.findIndex(o => o.value === item.value);
      if (activeIndex === -1) return '';
      return navigable[(activeIndex + direction + navigable.length) % navigable.length].label;
    }

    if (item.type === 'date-picker' || item.type === 'month-picker') {
      if (!item.date) return '';
      if (item.type === 'month-picker' || item.datePickerMode === 'month') {
        const next = addMonths(item.date, direction);
        const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        const monthName = this.translate.instant(`breadcrumb.monthsFull.${monthKeys[next.getMonth()]}`);
        return `${monthName} ${next.getFullYear()}`;
      }
      if (item.datePickerMode === 'week') {
        const next = startOfWeek(addWeeks(item.date, direction), { weekStartsOn: 1 });
        return `${format(next, 'd MMM')} – ${format(addDays(next, 6), 'd MMM yyyy')}`;
      }
      return format(addDays(item.date, direction), 'd MMM yyyy');
    }

    const siblings = this.getSiblings(index);
    if (!siblings || siblings.length < 2) return '';
    const activeIndex = siblings.findIndex(s => s.isCurrent);
    if (activeIndex === -1) return '';
    return siblings[(activeIndex + direction + siblings.length) % siblings.length].label ?? '';
  }

  /**
   * Keep the visual placeholder state in sync with the label fallback in
   * `getDropdownLabel`. The class must flip off only once options have loaded
   * and the current value resolves to an option — otherwise the button renders
   * the placeholder text without any visual cue that it is a placeholder.
   */
  protected isShowingDropdownPlaceholder(item: BreadcrumbItem): boolean {
    if (!item.value || !item.options) return true;
    return !item.options.some(opt => opt.value === item.value);
  }

  protected onDropdownSelect(item: BreadcrumbItem, option: BreadcrumbDropdownOption, event: Event): void {
    event.stopPropagation();
    this.openDropdownIndex.set(null);

    if (item.onSelect) {
      item.onSelect(option.value);
    }
  }
}
